import React, { useEffect, useRef, useState } from 'react';
import { Activity, Bot, Square, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAppMode } from '../../contexts/AppModeContext';
import { useNycTime } from '../../hooks/useNycTime';
import MediaBloxLogo from '../MediaBloxLogo';
import './Header.css';

export const HEADER_HEIGHT = 56;

function getDisplayName(user) {
  if (!user?.email) return 'User';
  const local = user.email.split('@')[0];
  const part = local.split(/[._-]/)[0];
  return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
}

function AgentHeaderTimer({ onStop }) {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    if (!startRef.current) startRef.current = Date.now();
    const tick = () => {
      setElapsed(Math.floor((Date.now() - (startRef.current ?? Date.now())) / 1000));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const hh = String(Math.floor(elapsed / 3600)).padStart(2, '0');
  const mm = String(Math.floor((elapsed % 3600) / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');

  return (
    <div className="hidden sm:flex items-center gap-2.5 mr-4">
      <div className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-mono tabular-nums rounded bg-[#FF003C]/[0.08] text-[#FF003C]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#FF003C] animate-pulse shadow-[0_0_6px_rgba(255,0,60,0.6)]" />
        <span className="tracking-wider">
          {hh}:{mm}:{ss}
        </span>
      </div>
      <button
        type="button"
        onClick={onStop}
        className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] rounded bg-[#FF003C]/[0.08] text-[#FF003C] hover:bg-[#FF003C]/[0.15] transition-all duration-200"
      >
        <Square size={9} className="fill-current" />
        Stop Agent
      </button>
    </div>
  );
}

const Header = ({
  onOpenAgentSettings,
  agentTimerActive = false,
  onStartAgent,
  onStopAgent,
}) => {
  const { user } = useAuth();
  const { isAgent } = useAppMode();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const { period, nycTime, nycDate } = useNycTime();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const displayName = getDisplayName(user);
  const profileImageUrl = user?.image
    ? `https://mediablox-submission-pipeline.s3.amazonaws.com/images/users/${user.image}`
    : null;

  const openProfileOrAgent = () => {
    if (isAgent && onOpenAgentSettings) {
      onOpenAgentSettings();
      return;
    }
    navigate('/profile');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[60] px-6 py-3.5 flex items-center justify-between transition-all duration-500 ease-out border-b border-white/[0.08] ${
        isScrolled
          ? 'bg-black/72 backdrop-blur-[35px] shadow-[0_8px_24px_rgba(0,0,0,0.3)]'
          : 'bg-black/20 backdrop-blur-[25px]'
      }`}
    >
      <div className="flex items-center gap-4 animate-[slideIn_1.2s_cubic-bezier(0.16,1,0.3,1)_0.1s_both]">
        <div className="flex items-center gap-3 select-none">
          <MediaBloxLogo className="header-logo-group" size={20} />
          <span className="text-xs font-bold uppercase tracking-wider text-white border-l border-white/25 pl-3">
            DATA STUDIO
          </span>
        </div>
      </div>

      <div className="flex items-center gap-4 animate-[slideIn_1s_cubic-bezier(0.16,1,0.3,1)_0.6s_both]">
        {user && (
          <div className="flex items-center gap-2">
            {!isAgent && (
              <>
                <span className="hidden sm:inline mr-4 text-[10px] font-medium text-gray-500 tabular-nums tracking-wide">
                  <Activity size={12} className="text-[#39FF14] inline-block mr-1.5" />
                  {nycTime} <span className="text-gray-600">(EST),</span>{' '}
                  <span className="text-gray-600">{nycDate}</span>
                </span>
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  className="hidden sm:block text-[11px] text-gray-300 hover:text-white transition-colors"
                >
                  {period}, <span className="font-semibold text-[#C4AB04]">{displayName}</span>
                </button>
              </>
            )}
            {isAgent && (
              <>
                {!agentTimerActive && onStartAgent && (
                  <button
                    type="button"
                    onClick={onStartAgent}
                    className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] rounded bg-[#00FFFF]/[0.1] text-[#00FFFF] hover:bg-[#00FFFF]/[0.18] transition-all mr-2"
                  >
                    Activate Agent
                  </button>
                )}
                {agentTimerActive && onStopAgent && <AgentHeaderTimer onStop={onStopAgent} />}
                <button
                  type="button"
                  onClick={openProfileOrAgent}
                  className="hidden sm:block text-[11px] text-[#00FFFF]/70 hover:text-[#00FFFF] transition-colors font-mono tracking-wide"
                >
                  FredBot
                </button>
              </>
            )}
            <button
              type="button"
              onClick={openProfileOrAgent}
              className={`w-9 h-9 rounded-full overflow-hidden border transition-colors focus:outline-none focus:ring-2 ${
                isAgent
                  ? 'border-[#00FFFF]/50 hover:border-[#00FFFF] focus:ring-[#00FFFF] bg-[#00FFFF]/[0.08]'
                  : 'border-white/[0.08] hover:border-[#C4AB04] focus:ring-[#C4AB04]'
              }`}
              aria-label={isAgent ? 'Open agent settings' : 'Open user settings'}
              title={isAgent ? 'Agent settings' : 'User settings'}
            >
              {profileImageUrl && !isAgent ? (
                <img src={profileImageUrl} alt="User profile" className="w-full h-full object-cover" />
              ) : isAgent ? (
                <Bot className="w-full h-full text-[#00FFFF] p-1.5" />
              ) : (
                <div className="w-full h-full bg-white/[0.06] flex items-center justify-center">
                  <User size={16} className="text-gray-400" />
                </div>
              )}
            </button>
            {!isAgent && user?.role === 'admin' && (
              <span className="hidden sm:inline text-[9px] font-bold uppercase tracking-[0.1em] text-white/[0.17] bg-white/[0.05] rounded px-1.5 py-0.5">
                Admin
              </span>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
